import React, { FC, useState } from 'react';
import FontBody1 from '../../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';
import Card from '../../../../../components/layouts/card/card';
import customizationStore from '../../../../../stores/customizations/customization-store';
import { FormField } from '../../../../../components/form-field/form-field';
import { PORTAL_BACKGROUND_SIZE, PORTAL_BACKGROUND_TYPE } from '../../../../../models/User';
import backgrounds_preview from '../../../../../images/previews/backgrounds.svg';
import ControlGroupWithPreview
	from '../../../../../components/layouts/control-group-with-preview/control-group-with-preview';
import ListLayout from '../../../../../components/layouts/list-layout/list-layout';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import { observer } from 'mobx-react';
import { IOption, SelectDropdown } from '../../../../../components/inputs/select-dropdown/select-dropdown';
import ColorPickerInput from '../../color-picker-input/color-picker-input';
import { Input } from '../../../../../components/inputs/input/input';
import IconImageRequest from '../../../../../components/icons/icon-image-request/icon-image-request';
import FileUpload from '../../../../../components/file-upload/file-upload';
import { ButtonIcon } from '../../../../../components/inputs/button-icon/button-icon';
import IconDelete from '../../../../../components/icons/icon-delete/icon-delete';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import './background-upload.scss';
import './background-preview.scss';

const BackgroundCard: FC = observer(() => {

	const classicCustomizationStore = customizationStore.getClassicStore();
	const [isExpanded, setIsExpanded] = useState(false);

	const backgroundTypeOptions: IOption[] = [
		{ label: 'Color', value: PORTAL_BACKGROUND_TYPE.COLOR },
		{ label: 'Image', value: PORTAL_BACKGROUND_TYPE.IMAGE }
	];

	const getBackgroundTypeValue = (value: PORTAL_BACKGROUND_TYPE): IOption | undefined => {
		return backgroundTypeOptions.find((option) => option.value === value);
	};

	const backgroundSizeOptions: IOption[] = [
		{ label: 'Stretch', value: PORTAL_BACKGROUND_SIZE.STRETCH },
		// {label: 'Fit', value: PORTAL_BACKGROUND_SIZE.FIT}, //Hiding this option untill we can figure out how to make it look good
		{ label: 'Repeat', value: PORTAL_BACKGROUND_SIZE.REPEAT }
	];

	const getBackgroundSizeValue = (value: PORTAL_BACKGROUND_SIZE): IOption | undefined => {
		return backgroundSizeOptions.find((option) => option.value === value);
	};

	const uploadTrackingPortalBackgroundImage = (fileData: File, fileContent: string) => {
		if (fileData && fileContent) {
			classicCustomizationStore.uploadTrackingPortalBackgroundImage(fileData.type, fileContent.split(',')[1]);
		}
	};

	return (
		<Card
			toolbar={{
				title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Backgrounds</FontBody1>
			}}
			isExpanded={isExpanded}
			expandCallback={setIsExpanded}
		>
			<FormField
				label="Tracking portal"
				instructions="Customize the background of your tracking portal"
				children={null}
			/>
			<ControlGroupWithPreview
				hasTopBorder={false}
				gridStyle={{childrenWidth: {width: 1}, previewWidth: {width: 1}}}
				preview={<div className="preview"><img src={backgrounds_preview} alt=""/></div>}
			>
				<ListLayout>
					<FormFieldLine label="Page background">
						<SelectDropdown
							options={backgroundTypeOptions}
							value={getBackgroundTypeValue(classicCustomizationStore.trackingPortalBackgroundType)}
							onChange={(value) => classicCustomizationStore.setTrackingPortalBackgroundType(value?.value as PORTAL_BACKGROUND_TYPE)}
						/>
					</FormFieldLine>
					{
						classicCustomizationStore.trackingPortalBackgroundType === PORTAL_BACKGROUND_TYPE.COLOR && (
							<FormFieldLine label="Page background color">
								<ColorPickerInput
									value={classicCustomizationStore.trackingPortalBackgroundValue ?? undefined}
									onChange={classicCustomizationStore.setTrackingPortalBackgroundValue}
								/>
							</FormFieldLine>
						)
					}
					{
						classicCustomizationStore.trackingPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE && (
							<>
								<div className="background-upload-container">
									<FormFieldLine label="Page background image">
										<div className="background-upload-input">
											<div className="upload-icon-container">
												<IconImageRequest color="var(--color-semantic-3)" />
											</div>
											<FileUpload
												uploadFile={(fileData, fileContent) => uploadTrackingPortalBackgroundImage(fileData, fileContent as string)}
												buttonText={"Upload image"}
												readType="dataUrl"
												fileTypes={['image/jpeg', 'image/png', 'image/gif']}
												acceptedFileFormats=".jpg,.jpeg,.png,.gif"
												disabled={classicCustomizationStore.isSaving()}
											/>
										</div>
									</FormFieldLine>
									{
										classicCustomizationStore.trackingPortalBackgroundValue && (
											<div className="background-preview-container">
												<FontBody2 color={FONT_COLOR.PRIMARY3_50}>
													Image size {classicCustomizationStore.trackingPortalBackgroundImageDimensions}
												</FontBody2>
												<div className="background">
													<img src={classicCustomizationStore.trackingPortalBackgroundValue} alt="background" />
													<ButtonIcon
														Component={IconDelete}
														color="var(--color-accent-3)"
														onClick={classicCustomizationStore.deleteTrackingPortalBackgroundImage}
														disabled={classicCustomizationStore.isSaving()}
													/>
												</div>
											</div>
										)
									}
								</div>
								<FormFieldLine label="Page background size">
									<SelectDropdown
										options={backgroundSizeOptions}
										value={getBackgroundSizeValue(classicCustomizationStore.trackingPortalBackgroundSize)}
										onChange={(value) => classicCustomizationStore.setTrackingPortalBackgroundSize(value?.value as PORTAL_BACKGROUND_SIZE)}
									/>
								</FormFieldLine>
							</>
						)
					}

					<FormFieldLine label="Portal background">
						<ColorPickerInput
							value={classicCustomizationStore.trackingPortalForegroundColor}
							onChange={classicCustomizationStore.setTrackingPortalForegroundColor}
						/>
					</FormFieldLine>
					<FormFieldLine label="Portal background opacity">
						<Input
							type="number"
							min={0} max={100}
							value={classicCustomizationStore.trackingPortalForegroundOpacity}
							onChange={classicCustomizationStore.setTrackingPortalForegroundOpacity}
						/>
					</FormFieldLine>

				</ListLayout>
			</ControlGroupWithPreview>
		</Card>
	);
});

export default BackgroundCard;
